import { useState, useRef, useEffect, useCallback } from "react";
import { FaTimes } from "react-icons/fa";
import "./MainInput.scss";
import SearchDropdown from "../../Dropdowns/SearchDropdown/SearchDropdown";
import AirportDropdown from "../../Dropdowns/AirportDropdown/AirportDropdown";
import DateDropdown from "../../Dropdowns/DateDropdown/DateDropdown";
import TravelersDropdown from "../../Dropdowns/TravelersDropdown/TravelersDropdown";
import AirportPopUp from "../../PopUps/AirportPopUp/AirportPopUp";
import SearchPopUp from "../../PopUps/SearchPopUp/SearchPopUp";
import DatePopUp from "../../PopUps/DatePopUp/DatePopUp";
import TravelersPopUp from "../../PopUps/TravelersPopUp/TravelersPopUp";
import { useSelector } from "react-redux";
import { selectSearchFilters } from "../../../reducers/searchSlice";
import { useMountEffect } from "../../../utils";
import { formatDateRangeDisplay } from "../../../reducers/utils";
import { useIntl } from "react-intl";

const MainInput = ({ type = "text", icon, textPlaceholder, onChange, value = "", options = [], popUpView = false, activeMode = "PAC", forceOpen = false, onOpenChange = null }) => {
  const intl = useIntl();
  const { searchRegions, searchAirports, searchDate, searchPersons } = useSelector(selectSearchFilters);
  const roomallocation = useSelector((state) => state.search.roomallocation);
  const alternativeRegionName = useSelector((state) => state.offers.alternativeRegionName);
  const desiredHotels = useSelector((state) => state.search.desiredHotels);
  const activeModeSel = useSelector((state) => state.search.activeMode);
  const isACC = activeModeSel === "ACC";
  const [isExpanded, setIsExpanded] = useState(false);

  const syncTravelersLabel = () => {
    const alloc = Array.isArray(roomallocation) && roomallocation.length > 0 ? roomallocation : null;
    let label;
    let optionValue;
    if (alloc) {
      const childCount = alloc.reduce((s, r) => s + (Array.isArray(r.childrenAges) ? r.childrenAges.length : 0), 0);
      const adultCount = alloc.reduce((s, r) => s + (r.adults || 0), 0);
      const totalGuests = adultCount + childCount;
      const guestText = totalGuests === 1 ? intl.formatMessage({ id: "personSingular" }) : intl.formatMessage({ id: "personsPlural" });
      const roomText = alloc.length === 1 ? intl.formatMessage({ id: "roomSingular" }) : intl.formatMessage({ id: "roomsPlural" });
      label = `${totalGuests} ${guestText}, ${alloc.length} ${roomText}`;
      optionValue = { ...searchPersons, adults: adultCount, children: childCount, childrenAges: alloc.flatMap((r) => r.childrenAges || []), rooms: alloc.length, roomAllocation: alloc };
    } else if (searchPersons) {
      const totalGuests = (searchPersons?.adults || 0) + (searchPersons?.children || 0);
      const roomText = (searchPersons?.rooms || 0) === 1 ? intl.formatMessage({ id: "roomSingular" }) : intl.formatMessage({ id: "roomsPlural" });
      const guestText = totalGuests === 1 ? intl.formatMessage({ id: "personSingular" }) : intl.formatMessage({ id: "personsPlural" });
      label = `${totalGuests} ${guestText}, ${searchPersons?.rooms} ${roomText}`;
      optionValue = { ...searchPersons };
    }
    if (label === undefined || label === inputValue) return;
    handleOptionSelect({ label, value: optionValue });
  };

  useEffect(() => {
    if (type === "travelers") syncTravelersLabel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomallocation, searchPersons]);

  useMountEffect(() => {
    if (type == "search") {
      setSelectedOption(searchRegions || []);
    }
    if (type == "airport") {
      setSelectedOption(searchAirports || []);
    }

    if (type == "date") {
      if (searchDate) {
        const dateRange = formatDateRangeDisplay(searchDate.start, searchDate.end, searchDate.duration || 7, searchDate.durationRange || null, isACC, intl);
        handleOptionSelect({
          label: dateRange,
          value: {
            start: searchDate.start,
            end: searchDate.end,
            duration: searchDate.duration || 7,
            durationRange: searchDate.durationRange || null,
            weekPreset: searchDate.weekPreset || null,
            weekdays: searchDate.weekdays || null,
          },
        });
      }
    }

    if (type == "travelers") {
      syncTravelersLabel();
    }
  });

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(type === "search" || type === "airport" ? "" : value);

  // Handle forceOpen prop for auto-advance feature
  useEffect(() => {
    if (forceOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [forceOpen]);

  // Notify parent when isOpen changes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  const [selectedOption, setSelectedOption] = useState(type === "search" || type === "airport" ? [] : null);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Cleanup debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (type === "search" || type === "airport") {
      if (Array.isArray(value)) {
        setSelectedOption(value);
      }
    } else {
      if (value !== inputValue) {
        setInputValue(value);
        setSelectedOption(value);
      }
    }
  }, [value, type]);

  useEffect(() => {
    if (type === "date" && searchDate?.start && searchDate?.end) {
      const dateRange = formatDateRangeDisplay(searchDate.start, searchDate.end, searchDate.duration || 7, searchDate.durationRange || null, isACC, intl);
      setInputValue(dateRange);
    }
  }, [isACC]);

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;

    if (type === "search" || type === "airport") {
      // Sofort UI aktualisieren
      setSearchQuery(newValue);
      setInputValue(newValue);

      // Debouncing für onChange (300ms Verzögerung)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (onChange) {
          onChange(newValue, selectedOption);
        }
      }, 300);
    } else {
      setInputValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    }
  };

  const handleOptionSelect = (option, singleOption) => {
    if (type === "search" || type === "airport") {
      const newSelection = Array.isArray(option) ? option : [];
      setSelectedOption(newSelection);

      if (onChange) {
        onChange(inputValue, newSelection);
      }
      return;
    }

    let displayValue = "";

    if (type === "date") {
      displayValue = option.label || "";
    } else if (type === "travelers") {
      displayValue = option.label || "";
    } else if (typeof option === "string") {
      displayValue = option;
    } else if (option && option.label) {
      displayValue = option.label;
    }

    setInputValue(displayValue);
    setSelectedOption(option);
    setIsOpen(false);

    if (onChange) {
      onChange(displayValue, option);
    }
  };

  const handleRemoveSelectedItem = (itemToRemove, e) => {
    e.stopPropagation();

    if (type === "airport" && Array.isArray(selectedOption)) {
      const newSelection = selectedOption.filter((item) => !(item.value === itemToRemove.value && item.label === itemToRemove.label));

      setSelectedOption(newSelection);

      if (onChange) {
        onChange(inputValue, newSelection);
      }
    }

    if (type === "search") {
      setSelectedOption([]);
      if (onChange) {
        onChange(inputValue, []);
      }
    }
  };

  const handleInputFocus = () => {
    if (type === "date" || type === "travelers") {
      setIsOpen(true);
    }
  };

  const handleInputMouseDown = (e) => {
    // Fix for Firefox: ensure popup opens on first click for date and travelers inputs
    if (type === "date" || type === "travelers") {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const isReadOnly = () => {
    return type === "date" || type === "travelers";
  };

  const getPlaceholder = () => {
    if (type === "search") {
      return intl.formatMessage({ id: "countryRegionOrHotelShort" });
    } else if (type === "airport") {
      return intl.formatMessage({ id: "selectAirport" });
    }
    return textPlaceholder || "";
  };

  const getDisplayValue = () => {
    if (type === "search" || type === "airport") {
      // KIsha-Wunschhotel-Suche: nach dem Neuladen per ?id= fehlt alternativeRegionName,
      // die Herkunft steht aber an desiredHotels[].src - statt Platzhalter oder
      // "8 Ziele ausgewaehlt" sagen, was gesucht wird.
      const kishaSearch = Array.isArray(desiredHotels) && desiredHotels.length > 0 && desiredHotels.every((d) => d?.src === "kisha");
      if (selectedOption && selectedOption.length > 0) {
        // KIsha/Deeplink Label hat Vorrang (z.B. "KIshas Empfehlungen")
        if (type === "search" && alternativeRegionName) {
          return alternativeRegionName;
        }
        if (type === "search" && kishaSearch) {
          return intl.formatMessage({ id: "kishaTopPicks", defaultMessage: "KIshas Top-Empfehlungen" });
        }
        if (selectedOption.length === 1) {
          return selectedOption[0].label;
        } else {
          const typeText = type === "search" ? intl.formatMessage({ id: "destinationsSelected" }) : intl.formatMessage({ id: "airportsSelectedCount" });
          return `${selectedOption.length} ${typeText}`;
        }
      }
      // Kein Ziel ausgewaehlt, aber eine KIsha-Suche: nicht den leeren Platzhalter zeigen.
      if (type === "search") {
        if (alternativeRegionName) return alternativeRegionName;
        if (kishaSearch) return intl.formatMessage({ id: "kishaTopPicks", defaultMessage: "KIshas Top-Empfehlungen" });
      }
      return "";
    }
    return inputValue;
  };

  const renderDropdown = () => {
    // If popUpView is true, don't render dropdowns (popups are rendered separately)
    if (popUpView || !isOpen) return null;

    switch (type) {
      case "search":
        return <SearchDropdown ref={dropdownRef} onSelect={handleOptionSelect} searchValue={inputValue} selectedValue={selectedOption || []} onClose={() => setIsOpen(false)} />;
      case "airport":
        return <AirportDropdown ref={dropdownRef} onSelect={handleOptionSelect} searchValue={inputValue} selectedValue={selectedOption || []} onClose={() => setIsOpen(false)} />;
      case "date":
        return <DateDropdown ref={dropdownRef} onSelect={handleOptionSelect} selectedValue={selectedOption} onClose={() => setIsOpen(false)} />;
      case "travelers":
        return <TravelersDropdown ref={dropdownRef} onSelect={handleOptionSelect} selectedValue={selectedOption} onClose={() => setIsOpen(false)} />;
      default:
        return null;
    }
  };

  const renderPopUp = () => {
    if (!popUpView) return null;

    switch (type) {
      case "search":
        return <SearchPopUp isOpen={isOpen} onClose={() => setIsOpen(false)} onSelect={handleOptionSelect} selectedValue={selectedOption || []} activeMode={activeMode} />;
      case "airport":
        return <AirportPopUp isOpen={isOpen} onClose={() => setIsOpen(false)} onSelect={handleOptionSelect} selectedValue={selectedOption || []} />;
      case "date":
        return <DatePopUp isOpen={isOpen} onClose={() => setIsOpen(false)} onSelect={handleOptionSelect} selectedValue={selectedOption} />;
      case "travelers":
        return <TravelersPopUp isOpen={isOpen} onClose={() => setIsOpen(false)} onSelect={handleOptionSelect} selectedValue={selectedOption} />;
      default:
        return null;
    }
  };

  const renderSelectedItems = () => {
    if ((type !== "search" && type !== "airport") || !Array.isArray(selectedOption) || selectedOption.length === 0) {
      return null;
    }

    const tooMany = selectedOption.length > 3;
    const visibleItems = tooMany && !isExpanded ? selectedOption.slice(0, 3) : selectedOption;
    const hiddenCount = selectedOption.length - 3;

    return (
      <div className="selected-items">
        {visibleItems.map((item, index) => (
          <div key={`${item.value}-${item.label}-${index}`} className="selected-item">
            <span className="selected-item-label">{item.label}</span>
            <button className="selected-item-remove" onClick={(e) => handleRemoveSelectedItem(item, e)} type="button" aria-label={intl.formatMessage({ id: "removeItem" }, { label: item.label })}>
              <FaTimes />
            </button>
          </div>
        ))}

        {tooMany && (
          <div
            className="selected-item selected-item-summary"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded
              ? intl.formatMessage({
                  id: "collapseSelected",
                  defaultMessage: "Auswahl ausblenden",
                })
              : `${hiddenCount > 0 ? selectedOption.length : 3} ${intl.formatMessage({
                  id: "airportsSelectedCount",
                  defaultMessage: "Flughäfen ausgewählt",
                })}`}
          </div>
        )}
      </div>
    );
  };

  const shouldShowSelectedItemsBelow = () => {
    return (type === "search" || type === "airport") && selectedOption && selectedOption.length > 0;
  };

  // const shouldShowInput = () => {
  //   return !shouldShowSelectedItemsBelow() || inputValue.trim() !== "";
  // };

  return (
    <>
      <div className="main_input_wrapper" ref={inputRef}>
        <div className="main_input" onClick={handleInputClick}>
          <div className="main_input-icon">{icon}</div>
          <div className="main_input-details">
            <label htmlFor="mainInput">{textPlaceholder}</label>

            <input
              type="text"
              value={getDisplayValue()}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onMouseDown={handleInputMouseDown}
              readOnly={isReadOnly()}
              placeholder={getPlaceholder()}
              style={{
                border: "none",
                borderWidth: 0,
                borderRadius: 0,
                outline: "none",
                boxShadow: "none",
                background: "transparent",
                backgroundColor: "transparent",
                padding: 0,
                margin: 0,
                width: "100%",
                height: "auto",
                minHeight: "unset",
                fontSize: "13px",
                fontWeight: 400,
                color: "#333333",
                lineHeight: 1.4,
                cursor: "pointer",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                WebkitAppearance: "none",
                MozAppearance: "none",
                appearance: "none",
              }}
            />
          </div>
        </div>

        {renderDropdown()}
      </div>

      {renderPopUp()}
    </>
  );
};

export default MainInput;
